import { useState, useMemo, useCallback } from "react";
import axios from "axios";
import { globalCountryDetailsDictionary } from "@core/global/global-country-details";
import { globalAPIDictionary, searchPhraseDictionary } from "@core/global/global-country-dictionary";
import { getApiUrl } from "@utils/baseUrl";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/browser";
import { TRADE_DIRECTIONS } from "@containers/shipments/utils";
import { getCountryUsingUrl } from "@containers/shipments/utils/helper";
import { generateQueryString } from "@core/utility/utility";

export const useRecentAutoSuggestSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);

  const getCountryInfo = useMemo(() => {
    const currentPath = window.location.pathname;
    return Object.values(globalCountryDetailsDictionary)?.find((country) => currentPath.includes(country?.countryPathName));
  }, []);

  const getRecentAutoSuggestSuggestions = useCallback(
    async (country, direction) => {
      if (!getCountryInfo && !country && !direction) {
        return;
      }
      let countryName = getCountryInfo?.countryShortHand || getCountryInfo?.country_CamelCase;
      let trade_direction = getCountryInfo?.manyCountryType;
      if (!(countryName && trade_direction)) {
        countryName = country;
        trade_direction = direction;
      }

      try {
        const { data } = await axios.post(globalAPIDictionary.getAutoSuggestRecentSearches, {
          country: countryName?.toLowerCase(),
          trade_direction: trade_direction?.toLowerCase(),
        });

        setSuggestions(data.data);
      } catch (error) {
        setSuggestions([]);
      }
    },
    [getCountryInfo]
  );

  return { suggestions, getRecentAutoSuggestSuggestions };
};

export const getProfileDetailsSummary = async (redirectingProfileType, redirectFrom, redirectingProfileId, profileId, mergeID, companyName, profileName) => {
  try {
    let reqObj = {
      category: redirectingProfileType,
      profile_type: redirectFrom,
      sub_category: "overview",
      _id: profileId ? profileId : Array.isArray(redirectingProfileId) ? redirectingProfileId : redirectingProfileId,
      slug: mergeID ? companyName : profileName,
    };
    if (mergeID) {
      reqObj.isMerged = true;
      reqObj.mergedID = +mergeID;
    }
    const { data } = await axios.post(getApiUrl("profileSummaryData"), reqObj);
    if (data && data.data) {
      let tempData = [];
      data.data.forEach((data) => {
        let tempObj = {};
        tempObj.count = data.doc_count;
        tempObj.country_code = data.countryCode;
        tempObj.country_name = data.country;
        tempObj.isSubscribed = data.isSubscribed;
        tempObj.is_favourite = data.is_favourite;
        tempObj.source = data.trade_direction.slice(0, -1);
        tempData.push(tempObj);
      });
      let profileName = data.profile_details.name ? (data.profile_details.country ? data.profile_details.name + ", " + data.profile_details.country : data.profile_details.name) : data.profile_details.trade_lane_name ? data.profile_details.trade_lane_name : "";
      return { companyProfileName: profileName, dataSourceAccess: [...tempData], profileSummaryDetails: data.profile_details };
    }
  } catch (err) {
    Sentry.captureException(err);
    toast.error("Something went wrong! Please try again.");
  }
};

export const getProfileDetails = async (redirectingProfileType, redirectFrom, redirectingProfileId, profileId, mergeID, companyName, profileName) => {
  try {
    let reqObj = {
      profile_type: redirectFrom,
      _id: profileId ? profileId : Array.isArray(redirectingProfileId) ? redirectingProfileId : redirectingProfileId,
      slug: mergeID ? companyName : profileName,
      category: redirectingProfileType,
    };
    if (mergeID) {
      reqObj.isMerged = true;
      reqObj.mergedID = +mergeID;
    }
    const { data } = await axios.post(getApiUrl("profileDetails"), reqObj);
    return { isFavourite: data?.data?.is_favourite, isEmailAlert: data?.data?.is_email_alert };
  } catch (err) {
    Sentry.captureException(err);
    toast.error("Something went wrong! Please try again.");
  }
};

export const addToFavourite = async (summaryData, tab) => {
  const requestObj = [
    {
      isAppendTags: true,
      is_merged: false,
      company_name: summaryData?.name || summaryData?.trade_lane_name,
      company_id: summaryData?.elastic_data_id[0],
      company_country: summaryData?.country,
      trade_direction: tab ? TRADE_DIRECTIONS[tab] : summaryData?.is_buyer ? "import" : "export",
      data_source: getCountryUsingUrl(),
    },
  ];

  try {
    await axios.post(getApiUrl("addFavouriteCompany"), requestObj);
    await saveSearchHandler(summaryData, tab);
    return true;
  } catch (error) {
    Sentry.captureException(error);
    toast.error("Failed to add company to favorites.");
  }
};

const saveSearchHandler = async (summaryData, tab) => {
  const requestObject = {
    company_name: summaryData.name || summaryData.trade_lane_name,
    company_id: summaryData.elastic_data_id[0],
    company_country: summaryData.country,
    trade_direction: tab ? TRADE_DIRECTIONS[tab] : summaryData.is_buyer ? "import" : "export",
    data_source: getCountryUsingUrl(),
    is_email_alert: true,
    frequency: 30,
    source: "company",
    url: window.location.href,
  };

  try {
    await axios.post(getApiUrl("saveSearch"), requestObject);
    toast.success("Email alert for this company has been successfully created. You can also view and modify the alerts from your Favorite Companies list in the Dashboard.");
  } catch (error) {
    Sentry.captureException(error);
    toast.error("Email alert for this company could not be created.");
  }
};

const cancelTokens = new Map();

const cancelPreviousRequest = (functionName) => {
  if (cancelTokens.has(functionName)) {
    cancelTokens.get(functionName).cancel("Operation canceled due to new request.");
    cancelTokens.delete(functionName);
  }
};

export const getProductKeywordAutoSuggest = async (query, selectedData) => {
  cancelPreviousRequest("getProductKeywordAutoSuggest");
  const source = axios.CancelToken.source();
  cancelTokens.set("getProductKeywordAutoSuggest", source);

  try {
    const queryParams = {
      search: encodeURIComponent(query),
      lang: "EN",
    };
    const queryString = generateQueryString(queryParams);
    const url = getApiUrl("getAutosuggestResultForProductKeyword") + "?" + queryString;
    const response = await axios.get(url, { cancelToken: source.token });
    const items = response?.data?.suggestions;

    const options = items.map((i) => ({
      value: i.value,
      label: i.label,
    }));

    selectedData.forEach((item) => {
      const itemToBeRemoved = { value: item.value, label: item.label };
      const findIndex = options.findIndex((a) => a.label === itemToBeRemoved.label);
      if (findIndex !== -1) {
        options.splice(findIndex, 1);
      }
    });
    return options;
  } catch (error) {
    if (!axios.isCancel(error)) {
      Sentry.captureException(error);
      toast.error("Something went wrong. Please try again later !!!");
    }
    return { loading: true };
  } finally {
    cancelTokens.delete("getProductKeywordAutoSuggest");
  }
};

export const getChemicalKeywordAutoSuggest = async (query, selectedData) => {
  cancelPreviousRequest("getChemicalKeywordAutoSuggest");
  const source = axios.CancelToken.source();
  cancelTokens.set("getChemicalKeywordAutoSuggest", source);

  try {
    const url = getApiUrl("getChemicalAutosuggest") + "?search=" + query;
    const response = await axios.get(url, { cancelToken: source.token });
    const items = response?.data?.suggestions;

    const options = items.map((i) => ({
      value: i.value,
      label: i.label,
    }));

    selectedData.forEach((item) => {
      const itemToBeRemoved = { value: item.value, label: item.label };
      const findIndex = options.findIndex((a) => a.value === itemToBeRemoved.value);
      if (findIndex !== -1) {
        options.splice(findIndex, 1);
      }
    });
    return options;
  } catch (error) {
    if (!axios.isCancel(error)) {
      Sentry.captureException(error);
      toast.error("Something went wrong. Please try again later !!!");
    }
    return { loading: true };
  } finally {
    cancelTokens.delete("getChemicalKeywordAutoSuggest");
  }
};

export const getCompanyNameAutoSuggest = async (query, countryCode, countryType, companyType, selectedData) => {
  cancelPreviousRequest("getCompanyNameAutoSuggest");
  const source = axios.CancelToken.source();
  cancelTokens.set("getCompanyNameAutoSuggest", source);

  try {
    let url = getApiUrl("companyProfiles/getCompanyAutoSuggest") + "?hint=" + encodeURIComponent(query) + "&type=" + companyType;
    if (countryCode && countryType) {
      url = url + "&country_code=" + countryCode + "&data_source=" + countryType;
    }
    const response = await axios.get(url, { cancelToken: source.token });
    const items = response?.data?.data ?? [];

    const options = items.map((i) => ({
      value: i.value,
      label: i.label.toString(),
      mongo_id: i?.mongo_id ?? "",
    }));

    selectedData.forEach((item) => {
      const itemToBeRemoved = { value: item.value, label: item.label };
      const findIndex = options.findIndex((a) => a.value === itemToBeRemoved.value);
      if (findIndex !== -1) {
        options.splice(findIndex, 1);
      }
    });
    return options;
  } catch (error) {
    if (!axios.isCancel(error)) {
      Sentry.captureException(error);
      toast.error("Something went wrong. Please try again later !!!");
    }
    return { loading: true };
  } finally {
    cancelTokens.delete("getCompanyNameAutoSuggest");
  }
};

export const getHsCodeAutoSuggest = async (query, country, selectedData) => {
  cancelPreviousRequest("getHsCodeAutoSuggest");
  const source = axios.CancelToken.source();
  cancelTokens.set("getHsCodeAutoSuggest", source);

  try {
    let enteredStr = query;
    let reg = /^[0-9]*[.]?[0-9]*$/;
    if (reg.test(enteredStr) === false) {
      return [];
    }
    const { data } = await axios.get(getApiUrl("getAutosuggestResult") + "?search=" + query + "&country=" + country, { cancelToken: source.token });
    const items = data?.suggestions;
    const options = items.map((i) => ({
      value: i,
      label: i,
    }));
    selectedData.forEach((item) => {
      const itemToBeRemoved = { value: item.value, label: item.label };
      const findIndex = options.findIndex((a) => a.value === itemToBeRemoved.value);
      if (findIndex !== -1) {
        options.splice(findIndex, 1);
      }
    });
    return options;
  } catch (error) {
    if (!axios.isCancel(error)) {
      Sentry.captureException(error);
      toast.error("Something went wrong. Please try again later !!!");
    }
    return { loading: true };
  } finally {
    cancelTokens.delete("getHsCodeAutoSuggest");
  }
};

export const getLogisticsPortsAutoSuggest = async (query, countryCode, countryType, companyType, selectedData) => {
  cancelPreviousRequest("getLogisticsPortsAutoSuggest");
  const source = axios.CancelToken.source();
  cancelTokens.set("getLogisticsPortsAutoSuggest", source);

  try {
    let url = getApiUrl("companyProfiles/getIntelAutoSuggest") + "?hint=" + encodeURIComponent(query) + "&type=" + companyType;
    if (countryCode && countryType) {
      url = url + "&country_code=" + countryCode + "&data_source=" + countryType;
    }
    const response = await axios.get(url, { cancelToken: source.token });
    const items = response?.data?.data ?? [];

    const options = items.map((i) => ({
      value: companyType === searchPhraseDictionary.product_category ? i.label : i.value,
      label: i.label.toString(),
      mongo_id: i?.mongo_id ?? "",
    }));

    selectedData.forEach((item) => {
      const itemToBeRemoved = { value: item.value, label: item.label };
      const findIndex = options.findIndex((a) => a.value === itemToBeRemoved.value);
      if (findIndex !== -1) {
        options.splice(findIndex, 1);
      }
    });
    return options;
  } catch (error) {
    if (!axios.isCancel(error)) {
      Sentry.captureException(error);
      toast.error("Something went wrong. Please try again later !!!");
    }
    return { loading: true };
  } finally {
    cancelTokens.delete("getLogisticsPortsAutoSuggest");
  }
};

export const getAllCategoryAutoSuggest = async (query, country, countryCode, countryType) => {
  cancelPreviousRequest("getAllCategoryAutoSuggest");
  const source = axios.CancelToken.source();
  cancelTokens.set("getAllCategoryAutoSuggest", source);

  try {
    if (!query) {
      return null;
    }
    let reqObj = {
      country: country ? country : "us",
      lang: "EN",
      country_code: countryCode ? countryCode : "US",
      data_source: countryType ? countryType : "import",
      search: encodeURIComponent(query),
    };
    let url = getApiUrl("allCategoryAutoSuggest") + "?" + generateQueryString(reqObj);
    const response = await axios.get(url, { cancelToken: source.token });
    return response?.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      Sentry.captureException(error);
      toast.error("Something went wrong. Please try again later !!!");
    }
    return { loading: true };
  } finally {
    cancelTokens.delete("getAllCategoryAutoSuggest");
  }
};

export const removeSearchTab = async (item) => {
  try {
    await axios.post(globalAPIDictionary.updateUserDearchStatus, {
      user_search: item.user_search,
      category_name: item.category_name,
    });
    return true;
  } catch (err) {
    Sentry.captureException(err);
    toast.error("Something went wrong. Please try again later !!!");
    return false;
  }
};

export const getHsCodeDetails = async () => {
  try {
    let reqObj = {
      hs_code: new URLSearchParams(window.location.search).get("hsCode"),
      data_source: "",
      country: "",
    };

    const res = await axios.post(getApiUrl("hscode_profile/getHSCodeProfileSummary"), reqObj);
    if (res?.data?.data) {
      const hsCodeData = res?.data?.data;
      return { dataSourceAccess: hsCodeData?.source_wise_shipment_list, profileName: hsCodeData?.hs_code };
    }
  } catch (err) {
    Sentry.captureException(err);
    toast.error("Something went wrong! Please try again.");
  }
};
